import { NavLink, useNavigate } from 'react-router-dom';
import { Search, Bell, Settings, LogOut } from 'lucide-react';
import { tenant } from '../../lib/tenant';
import type { ModuleRoute } from '../../routes/modules';
import type { Utilisateur } from '../../lib/auth-types';
import { estReserveAuxAdmins } from '../../lib/acces';
import { BacASableControl } from './BacASableControl';
import { FraicheurDonnees } from './FraicheurDonnees';
import { OngletsModules, type OngletModule } from './OngletsModules';

interface TopBarProps {
  readonly modules: ReadonlyArray<ModuleRoute>;
  readonly activeModulePath: string | null;
  readonly utilisateur: Utilisateur;
  readonly onLogout: () => void;
}

/** Toutes les icônes d'action de la barre parlent la même taille. */
const TAILLE_ICONE_ACTION = 18;

/**
 * TopBar 2 lignes inspirée de Vercel/GitHub :
 *   - Row 1 : logo + nom tenant + recherche + actions icon-only + utilisateur
 *   - Row 2 : onglets modules auto-découverts (défilement horizontal)
 *
 * Masquage des onglets admin (§3 `CONTRACT-AUTHENTIFICATION.md`) — CONFORT
 * uniquement, l'API refuse indépendamment (403) : ne rien construire de
 * sécuritaire sur ce filtre.
 *
 * Mise au point 10/08 (Ben : « le header n'est pas top »), à fonctions
 * constantes — rien n'a été déplacé ni retiré :
 *   - la recherche cède la place AVANT les actions quand la fenêtre rétrécit
 *     (elle est élastique, le bloc d'actions est incompressible) ;
 *   - une seule taille d'icône d'action, un seul pas d'espacement ;
 *   - la puce utilisateur tient sur une ligne, le rôle passe en infobulle ;
 *   - la rangée d'onglets défile dans son conteneur au lieu d'être tronquée
 *     (cf. `OngletsModules`).
 */
export function TopBar({ modules, activeModulePath, utilisateur, onLogout }: TopBarProps): JSX.Element {
  const navigate = useNavigate();
  const initials = tenant.shortName.slice(0, 1).toUpperCase();
  const estAdmin = utilisateur.role === 'admin' || utilisateur.estSuperAdmin;
  const roleLabel = utilisateur.estSuperAdmin ? 'Super admin' : utilisateur.role === 'admin' ? 'Admin' : 'Utilisateur';
  const visibleModules = modules.filter((m) => !m.hidden && (estAdmin || !estReserveAuxAdmins(m.path)));
  const userInitials =
    utilisateur.nom
      .trim()
      .split(/\s+/)
      .map((part) => part[0])
      .slice(0, 2)
      .join('')
      .toUpperCase() || utilisateur.email.slice(0, 1).toUpperCase();

  // Réglages (16/08, demande Ben) : quitte la barre d'onglets, s'ouvre depuis
  // la roue dentée ci-dessous. `moduleReglages` porte aussi le droit d'accès
  // (même filtre `visibleModules` que les autres onglets réservés admin).
  const moduleReglages = visibleModules.find((m) => m.path === 'admin');
  // Pilotage achats (16/08, demande Ben) : même fenêtre qu'Accueil (cf.
  // `routes/home.tsx`, qui redirige les admins de `/` vers `/pilotage-achats`)
  // → un seul onglet, « Accueil ». L'onglet reste actif même une fois la
  // redirection effectuée, sinon aucun onglet ne s'allume sur cet écran.
  const onglets: ReadonlyArray<OngletModule> = [
    {
      to: '/',
      icon: 'layout-dashboard',
      label: 'Accueil',
      isActive: activeModulePath === null || activeModulePath === 'pilotage-achats',
    },
    ...visibleModules
      .filter((m) => m.path !== 'admin' && m.path !== 'pilotage-achats')
      .map((m) => ({
        to: `/${m.path}`,
        icon: m.icon,
        label: m.label,
        isActive: activeModulePath === m.path,
        ...(m.badge ? { badge: m.badge } : {}),
      })),
  ];

  return (
    <header
      className="sticky top-0 z-40 bg-white border-b border-slate-200"
      data-app-shell-chrome
    >
      {/* Row 1 — le `pr-3.5` compense le padding interne des boutons icône :
          le dernier glyphe s'aligne alors optiquement sur la gouttière de 20 px. */}
      <div className="h-12 pl-5 pr-3.5 flex items-center gap-3 border-b border-slate-100">
        <NavLink to="/" className="flex items-center gap-2.5 shrink-0" aria-label={`Accueil ${tenant.name}`}>
          {tenant.logoUrl ? (
            <img src={tenant.logoUrl} alt="" className="w-7 h-7 rounded-md" />
          ) : (
            <div
              className="w-7 h-7 rounded-md grid place-items-center text-white text-sm font-bold"
              style={{ background: 'var(--accent)' }}
              aria-hidden="true"
            >
              {initials}
            </div>
          )}
          <div className="leading-tight">
            <div className="text-slate-900 font-semibold text-sm">{tenant.shortName}</div>
            <div className="text-slate-400 text-[11px] hidden sm:block">Synapse AIOS</div>
          </div>
        </NavLink>

        {/* Zone élastique : la recherche s'étire jusqu'à 380 px et se comprime
            jusqu'à 180 px avant que le bloc d'actions (shrink-0) ne bouge. */}
        <div className="flex-1 min-w-0 flex justify-end">
          <button
            type="button"
            className="hidden md:flex items-center gap-2 w-full max-w-[380px] min-w-[180px] h-8 bg-slate-100 hover:bg-slate-200 px-3 rounded-md text-sm text-slate-500"
            aria-label="Recherche globale"
          >
            <Search size={15} className="shrink-0" aria-hidden="true" />
            <span className="truncate">Rechercher produit, fournisseur, lot…</span>
            <span className="ml-auto hidden lg:inline shrink-0 font-mono text-[11px] bg-white border border-slate-300 px-1.5 rounded">
              ⌘K
            </span>
          </button>
        </div>

        <div className="flex items-center gap-1.5 shrink-0">
          {/* Une seule zone parle de la fraîcheur des données, et c'est
              celle-ci — voir l'en-tête de `FraicheurDonnees` pour le partage de
              rôle avec le contrôle bac à sable, qui n'est plus qu'un geste. */}
          <FraicheurDonnees />
          <BacASableControl estAdmin={estAdmin} />

          <div className="flex items-center gap-0.5">
            <IconButton label="Notifications" hasBadge>
              <Bell size={TAILLE_ICONE_ACTION} />
            </IconButton>
            {/* Accès aux Réglages (16/08, demande Ben) : l'onglet a quitté la
                barre, la roue dentée mène directement à `/admin` — elle
                n'avait aucune autre fonction avant ce changement (bouton sans
                `onClick`). Masquée en pratique pour les non-admins puisque
                `moduleReglages` suit le même filtre que l'ancien onglet. */}
            <IconButton label="Paramètres" onClick={moduleReglages ? () => navigate('/admin') : undefined}>
              <Settings size={TAILLE_ICONE_ACTION} />
            </IconButton>
          </div>

          <div className="flex items-center gap-1 pl-2 ml-0.5 border-l border-slate-200">
            {/* Une ligne, jamais deux : le rôle et l'e-mail vivent dans l'infobulle,
                sur une seule zone de survol pour toute la puce. */}
            <div
              className="flex items-center gap-2 pr-1"
              title={`${utilisateur.nom} — ${roleLabel} — ${utilisateur.email}`}
            >
              <span className="hidden xl:block text-slate-700 text-[13px] font-medium truncate max-w-[150px]">
                {utilisateur.nom}
              </span>
              <div
                aria-hidden="true"
                className="w-7 h-7 rounded-full grid place-items-center text-white font-semibold text-[11px] shrink-0"
                style={{ background: 'var(--accent)' }}
              >
                {userInitials}
              </div>
            </div>
            <IconButton label="Se déconnecter" onClick={onLogout}>
              <LogOut size={TAILLE_ICONE_ACTION} />
            </IconButton>
          </div>
        </div>
      </div>

      {/* Row 2 — onglets modules */}
      <OngletsModules onglets={onglets} />
    </header>
  );
}

function IconButton({
  children,
  label,
  hasBadge,
  onClick,
}: {
  readonly children: React.ReactNode;
  readonly label: string;
  readonly hasBadge?: boolean;
  readonly onClick?: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={onClick}
      className="relative grid place-items-center w-8 h-8 rounded-md text-slate-500 hover:bg-slate-100 hover:text-slate-900"
    >
      {children}
      {hasBadge && (
        <span
          aria-hidden="true"
          className="absolute top-1.5 right-1.5 w-1.5 h-1.5 rounded-full ring-2 ring-white"
          style={{ background: 'var(--danger)' }}
        />
      )}
    </button>
  );
}
